import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useNotifications } from '@sequencehq/core-components'
import { useIntegrationsDomainContext } from 'modules/Integrations/communication'
import {
  IntegrationConfig,
  ResolvedIntegration
} from 'modules/Integrations/domain'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import invariant from 'tiny-invariant'
import { usePermissions } from 'lib/rbac'

type UseIntegrationDetailHeader = (props: {
  integration: ResolvedIntegration
  onClose?: () => void
}) => {
  features: {
    manage: {
      label: string
      visible: boolean
      disabled: boolean
      onClick: () => void
      onAuxClick: () => void
    }
    connect: {
      label: string
      visible: boolean
      disabled: boolean
      showSpinner: boolean
      onClick: () => void
    }
    documentation: {
      onClick: () => void
      disabled: boolean
    }
  }
  comingSoon: boolean
  statusLabel: IntegrationConfig['statusLabel']
}

export const useIntegrationDetailHeader: UseIntegrationDetailHeader = props => {
  const { can } = usePermissions()
  const ctx = useIntegrationsDomainContext()
  const navigate = useNavigate()
  const { onClose } = props
  const { displayNotification } = useNotifications()
  const flags = useFeatureFlags()

  const integration = useMemo(() => {
    return ctx.queries.resolvedIntegrations[props.integration.id]
  }, [ctx.queries.resolvedIntegrations, props.integration.id])

  const integrationFeatures = useMemo(() => {
    const features =
      ctx.queries.availableFeatures.integrations[props.integration.id]

    if (props.integration.id === 'Avalara') {
      return {
        ...features,
        canManage: {
          available: {
            visible: flags.showAvalaraIntegration,
            enabled: flags.showAvalaraIntegration
          }
        },
        // Avalara uses basic auth so the connect button is not needed
        // the credentials go into the management page
        canConnect: {
          available: {
            visible: false,
            enabled: false
          }
        }
      }
    }

    if (props.integration.id === 'NetSuite') {
      return {
        ...features,
        canManage: {
          available: {
            visible: flags.useNetsuite,
            enabled: flags.useNetsuite
          }
        },
        // NetSuite management lives on the configuration screen, not an OAuth
        // connect flow
        canConnect: {
          available: {
            visible: false,
            enabled: false
          }
        }
      }
    }

    if (props.integration.id === 'Numeral') {
      return {
        ...features,
        canManage: {
          available: {
            visible: true,
            enabled: true
          }
        },
        // Numeral uses API key auth so the connect button is not needed
        // the credentials go into the management page
        canConnect: {
          available: {
            visible: false,
            enabled: false
          }
        }
      }
    }

    if (props.integration.id === 'Sphere') {
      return {
        ...features,
        canManage: {
          available: {
            visible: true,
            enabled: true
          }
        },
        // Sphere uses API key auth so the connect button is not needed
        // the credentials go into the management page
        canConnect: {
          available: {
            visible: false,
            enabled: false
          }
        }
      }
    }

    if (props.integration.id === 'Anrok') {
      return {
        ...features,
        canManage: {
          available: {
            visible: true,
            enabled: true
          }
        },
        // API key flow → no connect button
        canConnect: {
          available: {
            visible: false,
            enabled: false
          }
        }
      }
    }

    if (props.integration.id === 'Rillet') {
      return {
        ...features,
        canManage: {
          available: {
            visible: true,
            enabled: true
          }
        },
        // Rillet uses API key auth so the connect button is not needed
        // the credentials go into the management page
        canConnect: {
          available: {
            visible: false,
            enabled: false
          }
        }
      }
    }

    if (props.integration.id === 'Attio') {
      return {
        ...features,
        canManage: {
          available: {
            visible: true,
            enabled: true
          }
        },
        canConnect: {
          available: {
            visible: false,
            enabled: false
          }
        }
      }
    }
    return features
  }, [ctx.queries.availableFeatures.integrations, props.integration.id, flags])

  invariant(integration, 'Integration not found')
  invariant(integrationFeatures, 'Integration features not found')

  const canUpdateIntegration = can('org.integration:update')

  const features = useMemo(() => {
    return {
      manage: {
        label: 'Manage connector',
        visible:
          canUpdateIntegration &&
          integrationFeatures.canManage.available.visible,
        disabled:
          !canUpdateIntegration &&
          !integrationFeatures.canManage.available.enabled,
        onAuxClick: () => {
          window.open(
            `/integration-management/${props.integration.id}`,
            '_blank'
          )
        },
        onClick: () => {
          navigate(`/integration-management/${props.integration.id}`)
          onClose?.()
        }
      },
      connect: {
        label: ctx.queries.rawData.editor.connecting
          ? `Connecting...`
          : `Connect to ${props.integration.title}`,
        showSpinner: ctx.queries.rawData.editor.connecting,
        visible:
          canUpdateIntegration &&
          integrationFeatures.canConnect?.available.visible,
        disabled:
          !canUpdateIntegration &&
          !integrationFeatures.canConnect?.available.enabled,
        onClick: async () => {
          const res = await ctx.mutators.external.out.connect(
            props.integration.id
          )
          if (res.success) {
            displayNotification(
              `Connected to ${props.integration.title} successfully`,
              {
                type: 'success'
              }
            )
            navigate(`/integration-management/${props.integration.id}`)
            onClose?.()
            return
          }

          displayNotification(
            `Could not connect to ${props.integration.title}`,
            {
              type: 'error'
            }
          )
        }
      },
      documentation: {
        onClick: () => {
          window.open(props.integration.documentationUrl, '_blank')
        },
        disabled: !props.integration.documentationUrl
      }
    }
  }, [
    ctx.queries.rawData.editor.connecting,
    props.integration.id,
    props.integration.title,
    onClose,
    ctx.mutators,
    navigate,
    props.integration.documentationUrl,
    integrationFeatures.canConnect?.available,
    integrationFeatures.canManage.available,
    displayNotification
  ])

  const statusLabel =
    props.integration.id === 'NetSuite' && !flags.useNetsuite
      ? 'COMING_SOON'
      : integration.statusLabel

  return {
    features,
    statusLabel,
    comingSoon: integration.isComingSoon
  }
}
